import { loadJson } from '../store/supportSettingsStore';
import { TRUSTYBYTES_LOGO_SVG_RAW } from '../components/TrustyBytesLogo';

export type BlockType =
  | 'header_banner'
  | 'greeting_intro'
  | 'action_metrics_card'
  | 'ticket_meta_card'
  | 'info_grid'
  | 'message_body'
  | 'cta_button'
  | 'what_happens_next'
  | 'priority_callout'
  | 'divider_line'
  | 'signature_block'
  | 'footer_bar';

export interface ActionMetricItem {
  label: string;
  valueToken: string;
  icon: 'ticket' | 'play' | 'arrow_up' | 'alert' | 'tag' | 'clock';
  color: string;
  badgeBg: string;
  iconColor: string;
}

export interface CardBlock {
  id: string;
  type: BlockType;
  title: string;
  visible: boolean;
  props?: {
    bannerStyle?: 'gradient_cyan_green' | 'solid_blue' | 'dark_navy' | 'emerald_success' | 'purple_gradient';
    brandName?: string;
    headerTopic?: string;
    showLogo?: boolean;
    showStatusBadge?: boolean;
    headerLayout?: 'standard_split' | 'subject_only';
    greetingText?: string;
    introText?: string;
    metrics?: ActionMetricItem[];
    metaCardTitle?: string;
    showAsset?: boolean;
    showOrg?: boolean;
    showPriority?: boolean;
    showStatus?: boolean;
    gridColumns?: number;
    gridFields?: Array<{ label: string; valueToken: string; color?: string }>;
    messageContent?: string;
    borderAccentColor?: 'blue' | 'emerald' | 'purple' | 'amber' | 'slate' | 'none';
    buttonLabel?: string;
    buttonUrl?: string;
    buttonColor?: 'blue' | 'green' | 'dark' | 'outline';
    buttonAlign?: 'left' | 'center' | 'right';
    calloutTitle?: string;
    calloutText?: string;
    calloutType?: 'info' | 'warning' | 'success' | 'danger';
    nextStepsHeading?: string;
    nextStepsText?: string;
    supportEmail?: string;
    salutation?: string;
    agentNameFormula?: string;
    teamNameFormula?: string;
    tagline?: string;
    showTagline?: boolean;
    footerSubtitle?: string;
    footerBadgeText?: string;
    footerUrl?: string;
    showFooterWave?: boolean;
    dividerSpacing?: 'sm' | 'md' | 'lg';
    dividerColor?: string;
  };
}

export interface CardTemplateConfig {
  id: string;
  name: string;
  ticketType: string;
  actionType: string;
  headerTopic: string;
  useCardLayout: boolean; // TRUE = Branded Graphic Card, FALSE = Normal Texting Style
  bannerStyle: 'gradient_cyan_green' | 'solid_blue' | 'dark_navy' | 'emerald_success' | 'purple_gradient';
  showLogo: boolean;
  brandName: string;
  organizationName: string;
  tagline: string;
  footerSubtitle: string;
  footerUrl: string;
  greetingText: string;
  introText: string;
  showInnerCard: boolean;
  innerMessageBody: string;
  showWhatHappensNext: boolean;
  whatHappensNextText: string;
  supportEmail: string;
  signoffSalutation: string;
  agentFormula: string;
  teamFormula: string;
  showTagline: boolean;
  showDisclaimer: boolean;
  disclaimerText: string;
  calloutTitle?: string;
  calloutText?: string;
  calloutType?: 'info' | 'warning' | 'success';
  customHtml?: string;
  blocks?: CardBlock[];
}

export interface TemplateInterpolationData {
  ticketId?: string;
  subject?: string;
  status?: string;
  currentAction?: string;
  nextAction?: string;
  priority?: string;
  clientName?: string;
  agentName?: string;
  relatedAsset?: string;
  organization?: string;
  brandName?: string;
  teamName?: string;
  queue?: string;
  date?: string;
  ticketLink?: string;
  supportEmail?: string;
  from?: string;
  fromEmail?: string;
  senderName?: string;
  message?: string;
  body?: string;
}

export const AVAILABLE_TOKENS = [
  { token: '$ticketID$', label: 'Ticket ID', desc: 'Ticket identifier (e.g. TB#01752)' },
  { token: '$subject$', label: 'Subject', desc: 'Ticket summary title' },
  { token: '$status$', label: 'Status', desc: 'Active ticket status (e.g. In-progress)' },
  { token: '$currentaction$', label: 'Current Action', desc: 'Current active workflow action (e.g. In-progress)' },
  { token: '$nextaction$', label: 'Next Action', desc: 'Next recommended workflow action (e.g. Escalate to Supplier)' },
  { token: '$priority$', label: 'Priority', desc: 'Urgent / High / Medium / Low' },
  { token: '$client$', label: 'Client / Requester', desc: 'Customer full name (e.g. Girikumaran M S)' },
  { token: '$agent$', label: 'Agent', desc: 'Assigned engineer (e.g. Girikumaran (You))' },
  { token: '$message$', label: 'Message Body / Note', desc: 'Agent response or action note body' },
  { token: '$relatedasset$', label: 'Related Asset', desc: 'Hardware/Asset (e.g. MacBook Pro AST#8829)' },
  { token: '$organization$', label: 'Organization', desc: 'Customer company domain' },
  { token: '$team$', label: 'Team', desc: 'Assigned service desk team (e.g. Team One)' },
  { token: '$queue$', label: 'Queue', desc: 'Support dispatch queue' },
  { token: '$date$', label: 'Date', desc: 'Current formatted date' },
  { token: '$ticketlink$', label: 'Ticket Link', desc: 'Direct portal URL to ticket' },
  { token: '$from$', label: 'From Address', desc: 'Full From line (e.g. Support Team <support@trustybytes.in>)' },
  { token: '$support_email$', label: 'Support Email', desc: 'Configured outbound email address' },
];

export const INCIDENT_DEFAULT_BLOCKS: CardBlock[] = [
  {
    id: 'blk-hdr-01',
    type: 'header_banner',
    title: 'Header Banner',
    visible: true,
    props: {
      brandName: 'Team One',
      headerTopic: '$subject$',
      bannerStyle: 'gradient_cyan_green',
      showLogo: true,
      showStatusBadge: false,
      headerLayout: 'standard_split'
    }
  },
  {
    id: 'blk-grt-02',
    type: 'greeting_intro',
    title: 'Salutation & Intro',
    visible: true,
    props: {
      greetingText: 'Hi $client$,',
      introText: 'Your TrustyBytes Support ticket has been updated. A service representative has arrived to address your issue.'
    }
  },
  {
    id: 'blk-act-03',
    type: 'action_metrics_card',
    title: '4-Column Action Metrics',
    visible: true,
    props: {
      metrics: [
        { label: 'Ticket No.', valueToken: '$ticketID$', icon: 'ticket', color: '#0f172a', badgeBg: '#e0f2fe', iconColor: '#0284c7' },
        { label: 'Current Action', valueToken: '$currentaction$', icon: 'play', color: '#16a34a', badgeBg: '#dcfce7', iconColor: '#16a34a' },
        { label: 'Next Action', valueToken: '$nextaction$', icon: 'arrow_up', color: '#0f172a', badgeBg: '#f3e8ff', iconColor: '#9333ea' },
        { label: 'Priority', valueToken: '$priority$', icon: 'alert', color: '#dc2626', badgeBg: '#ffe4e6', iconColor: '#dc2626' }
      ]
    }
  },
  {
    id: 'blk-msg-04',
    type: 'message_body',
    title: 'Assistance Note',
    visible: true,
    props: {
      messageContent: 'If you require any further assistance or have questions on this ticket then please reply to this email.',
      borderAccentColor: 'none'
    }
  },
  {
    id: 'blk-sgn-05',
    type: 'signature_block',
    title: 'Signature Block',
    visible: true,
    props: {
      salutation: 'Kind regards,',
      agentNameFormula: 'Team One',
      teamNameFormula: '',
      tagline: '',
      showTagline: false
    }
  },
  {
    id: 'blk-ftr-06',
    type: 'footer_bar',
    title: 'Branded Footer',
    visible: true,
    props: {
      footerSubtitle: 'One operational view for every team.',
      footerBadgeText: 'TEAMONE',
      footerUrl: 'teamone.trustybytes.in',
      showFooterWave: true
    }
  }
];

export const DEFAULT_CARD_TEMPLATES: CardTemplateConfig[] = [
  {
    id: 'tpl-incident-default',
    name: 'Incident Ticket (Default Branded Card)',
    ticketType: 'incident',
    actionType: 'all',
    headerTopic: '$subject$',
    useCardLayout: false, // Standard text layout matching DB active config
    bannerStyle: 'gradient_cyan_green',
    showLogo: true,
    brandName: 'TrustyBytes',
    organizationName: 'TrustyBytes Support',
    tagline: 'Your IT Support Partner',
    footerSubtitle: 'Your IT Support Partner',
    footerUrl: 'www.trustybytes.com',
    greetingText: 'Hi $client$,',
    introText: 'Your TrustyBytes Support ticket has been updated. A service representative has arrived to address your issue.',
    showInnerCard: false,
    innerMessageBody: 'If you require any further assistance or have questions on this ticket then please reply to this email.',
    showWhatHappensNext: false,
    whatHappensNextText: '',
    supportEmail: 'support@trustybytes.in',
    signoffSalutation: 'Kind regards,',
    agentFormula: 'Team One',
    teamFormula: '',
    showTagline: false,
    showDisclaimer: false,
    disclaimerText: '',
    customHtml: `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8"/>
  <meta name="viewport" content="width=device-width, initial-scale=1.0"/>
  <title>$subject$</title>
</head>
<body style="margin:0;padding:24px;background-color:#ffffff;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif;color:#1e293b;font-size:14.5px;line-height:1.6;">
  <p style="margin:0 0 16px 0;font-size:16px;font-weight:700;color:#0f172a;">Hi $client$,</p>
  <p style="margin:0 0 16px 0;color:#334155;">Your TrustyBytes Support ticket has been updated. A service representative has arrived to address your issue.</p>
  <div style="margin:20px 0;padding:14px 18px;background-color:#f8fafc;border-left:3px solid #0062D2;font-size:13.5px;border-radius:4px;">
    <p style="margin:0 0 6px 0;"><strong>Ticket No:</strong> $ticketID$ &nbsp;|&nbsp; <strong>Current Action:</strong> $currentaction$ &nbsp;|&nbsp; <strong>Next Action:</strong> $nextaction$ &nbsp;|&nbsp; <strong>Priority:</strong> $priority$</p>
    <p style="margin:0;"><strong>Subject:</strong> $subject$</p>
  </div>
  <p style="margin:16px 0;color:#334155;">If you require any further assistance or have questions on this ticket then please reply to this email.</p>
  <div style="margin-top:28px;padding-top:16px;border-top:1px solid #e2e8f0;font-size:14px;color:#475569;">
    <p style="margin:0 0 4px 0;">Kind regards,</p>
    <p style="margin:0;font-weight:700;color:#0f172a;font-size:15px;">Team One</p>
  </div>
</body>
</html>`,
    blocks: INCIDENT_DEFAULT_BLOCKS
  },
  {
    id: 'tpl-ack-generic',
    name: 'Universal Acknowledgement',
    ticketType: 'all',
    actionType: 'acknowledgement',
    headerTopic: 'INCIDENT ACKNOWLEDGEMENT',
    useCardLayout: true,
    bannerStyle: 'gradient_cyan_green',
    showLogo: true,
    brandName: 'Team One',
    organizationName: 'TrustyBytes Enterprise Support',
    tagline: 'One operational view for every team.',
    footerSubtitle: 'One operational view for every team.',
    footerUrl: 'teamone.trustybytes.in',
    greetingText: 'Hi $client$,',
    introText: 'Your ticket has been securely received by our enterprise operations center. A dedicated engineer is reviewing the diagnostic logs.',
    showInnerCard: false,
    innerMessageBody: 'If you require any further assistance or have questions on this ticket then please reply to this email.',
    showWhatHappensNext: false,
    whatHappensNextText: '',
    supportEmail: 'support@trustybytes.in',
    signoffSalutation: 'Kind regards,',
    agentFormula: 'Team One',
    teamFormula: '',
    showTagline: false,
    showDisclaimer: false,
    disclaimerText: '',
    blocks: INCIDENT_DEFAULT_BLOCKS
  },
  {
    id: 'tpl-update-status',
    name: 'In-Progress Progress Update',
    ticketType: 'all',
    actionType: 'update',
    headerTopic: 'TICKET PROGRESS UPDATE',
    useCardLayout: true,
    bannerStyle: 'solid_blue',
    showLogo: true,
    brandName: 'Team One',
    organizationName: 'TrustyBytes Support',
    tagline: 'One operational view for every team.',
    footerSubtitle: 'One operational view for every team.',
    footerUrl: 'teamone.trustybytes.in',
    greetingText: 'Hi $client$,',
    introText: 'Your ticket progress has been updated. A service representative is investigating your request.',
    showInnerCard: false,
    innerMessageBody: 'If you require any further assistance or have questions on this ticket then please reply to this email.',
    showWhatHappensNext: false,
    whatHappensNextText: '',
    supportEmail: 'support@trustybytes.in',
    signoffSalutation: 'Kind regards,',
    agentFormula: 'Team One',
    teamFormula: '',
    showTagline: false,
    showDisclaimer: false,
    disclaimerText: '',
    blocks: INCIDENT_DEFAULT_BLOCKS
  },
  {
    id: 'tpl-resolved-success',
    name: 'Ticket Resolved & Verified',
    ticketType: 'all',
    actionType: 'resolved',
    headerTopic: 'TICKET RESOLVED',
    useCardLayout: true,
    bannerStyle: 'emerald_success',
    showLogo: true,
    brandName: 'Team One',
    organizationName: 'TrustyBytes Support',
    tagline: 'One operational view for every team.',
    footerSubtitle: 'One operational view for every team.',
    footerUrl: 'teamone.trustybytes.in',
    greetingText: 'Hi $client$,',
    introText: 'We are pleased to inform you that your support ticket [$ticketID$] has been successfully resolved.',
    showInnerCard: false,
    innerMessageBody: 'Root cause was verified and full connectivity has been restored. If you have any further questions, please reply directly.',
    showWhatHappensNext: false,
    whatHappensNextText: '',
    supportEmail: 'support@trustybytes.in',
    signoffSalutation: 'Kind regards,',
    agentFormula: 'Team One',
    teamFormula: '',
    showTagline: false,
    showDisclaimer: false,
    disclaimerText: '',
    blocks: INCIDENT_DEFAULT_BLOCKS
  }
];

export function resolveActiveEmailTemplate(actionType?: string, ticketType?: string): CardTemplateConfig {
  const activeSaved = loadJson<CardTemplateConfig | null>('teamone_active_email_card_config', null);
  const allTemplates = loadJson<CardTemplateConfig[]>('teamone_email_card_templates_v4', DEFAULT_CARD_TEMPLATES);

  if (actionType) {
    const actLower = actionType.toLowerCase();
    const matched = allTemplates.find((t) => {
      const tAct = (t.actionType || '').toLowerCase();
      return tAct !== 'all' && (tAct === actLower || actLower.includes(tAct) || tAct.includes(actLower));
    });
    if (matched) return matched;
  }

  if (ticketType) {
    const typeLower = ticketType.toLowerCase();
    const matched = allTemplates.find((t) => {
      const tType = (t.ticketType || '').toLowerCase();
      return tType !== 'all' && (tType === typeLower || typeLower.includes(tType));
    });
    if (matched) return matched;
  }

  if (activeSaved && activeSaved.id) {
    return activeSaved;
  }

  return allTemplates[0] || DEFAULT_CARD_TEMPLATES[0];
}

export function generateBaseTemplateHtml(template: CardTemplateConfig): string {
  const isCard = template.useCardLayout !== false;

  if (!isCard) {
    const greeting = template.greetingText || 'Hi $client$,';
    const intro = template.introText || '';
    const body = template.innerMessageBody || '';
    const salutation = template.signoffSalutation || 'Kind regards,';
    const agent = template.agentFormula || 'Team One';

    return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8"/>
  <meta name="viewport" content="width=device-width, initial-scale=1.0"/>
  <title>${template.headerTopic || '$subject$'}</title>
</head>
<body style="margin:0;padding:24px;background-color:#ffffff;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif;color:#1e293b;font-size:14.5px;line-height:1.6;">
  <p style="margin:0 0 16px 0;font-size:16px;font-weight:700;color:#0f172a;">${greeting}</p>
  <p style="margin:0 0 16px 0;color:#334155;">${intro}</p>
  <div style="margin:20px 0;padding:14px 18px;background-color:#f8fafc;border-left:3px solid #0062D2;font-size:13.5px;border-radius:4px;">
    <p style="margin:0 0 6px 0;"><strong>Ticket No:</strong> $ticketID$ &nbsp;|&nbsp; <strong>Current Action:</strong> $currentaction$ &nbsp;|&nbsp; <strong>Next Action:</strong> $nextaction$ &nbsp;|&nbsp; <strong>Priority:</strong> $priority$</p>
    <p style="margin:0;"><strong>Subject:</strong> $subject$</p>
  </div>
  <p style="margin:16px 0;color:#334155;">${body.replace(/\n/g, '<br/>')}</p>
  <div style="margin-top:28px;padding-top:16px;border-top:1px solid #e2e8f0;font-size:14px;color:#475569;">
    <p style="margin:0 0 4px 0;">${salutation}</p>
    <p style="margin:0;font-weight:700;color:#0f172a;font-size:15px;">${agent}</p>
  </div>
</body>
</html>`;
  }

  // Branded card layout
  const blocks = (template.blocks && template.blocks.length > 0) ? template.blocks : INCIDENT_DEFAULT_BLOCKS;
  const renderedBlockHtmls = blocks
    .filter((b) => b.visible)
    .map((b) => {
      const p = b.props || {};
      switch (b.type) {
        case 'header_banner': {
          const bannerStyle = p.bannerStyle || template.bannerStyle || 'gradient_cyan_green';
          let bgCss = 'background:linear-gradient(135deg, #0284c7 0%, #06b6d4 45%, #22c55e 100%);';
          if (bannerStyle === 'solid_blue') bgCss = 'background-color:#0062D2;';
          if (bannerStyle === 'dark_navy') bgCss = 'background-color:#0f172a;';
          if (bannerStyle === 'emerald_success') bgCss = 'background-color:#16a34a;';
          if (bannerStyle === 'purple_gradient') bgCss = 'background:linear-gradient(135deg, #4f46e5 0%, #7c3aed 50%, #9333ea 100%);';

          return `    <!-- HEADER BANNER -->
    <table width="100%" border="0" cellpadding="0" cellspacing="0" style="${bgCss}border-top-left-radius:12px;border-top-right-radius:12px;color:#ffffff;">
      <tr>
        <td style="padding:20px 24px;">
          <table width="100%" border="0" cellpadding="0" cellspacing="0">
            <tr>
              <td width="36" valign="middle" style="padding-right:16px;">
                ${TRUSTYBYTES_LOGO_SVG_RAW('#0062D2', '#8CC63F')}
              </td>
              <td width="1" bgcolor="rgba(255,255,255,0.3)" style="font-size:0;line-height:0;">&nbsp;</td>
              <td valign="middle" style="padding-left:16px;padding-right:16px;" width="130">
                <div style="font-size:11px;color:#ffffff;opacity:0.85;text-transform:uppercase;letter-spacing:0.5px;">Ticket No.</div>
                <div style="font-size:15px;font-weight:800;color:#ffffff;letter-spacing:0.3px;">$ticketID$</div>
              </td>
              <td width="1" bgcolor="rgba(255,255,255,0.3)" style="font-size:0;line-height:0;">&nbsp;</td>
              <td valign="middle" style="padding-left:16px;">
                <div style="font-size:11px;color:#ffffff;opacity:0.85;text-transform:uppercase;letter-spacing:0.5px;">Subject</div>
                <div style="font-size:15px;font-weight:700;color:#ffffff;">$subject$</div>
              </td>
            </tr>
          </table>
        </td>
      </tr>
    </table>`;
        }

        case 'greeting_intro': {
          return `    <!-- GREETING & INTRO -->
    <div style="padding:28px 28px 12px 28px;">
      <h2 style="font-size:18px;font-weight:800;color:#0f172a;margin:0 0 12px 0;">${p.greetingText || template.greetingText || 'Hi $client$,'}</h2>
      <p style="font-size:14px;color:#334155;line-height:1.6;margin:0;">${p.introText || template.introText || ''}</p>
    </div>`;
        }

        case 'action_metrics_card': {
          return `    <!-- 4-COLUMN ACTION & STATUS METRICS CARD -->
    <div style="padding:16px 28px;">
      <table width="100%" border="0" cellpadding="0" cellspacing="0" style="background-color:#f0f4f9;border:1px solid #e2e8f0;border-radius:14px;text-align:center;">
        <tr>
          <td width="25%" style="padding:18px 10px;border-right:1px solid #e2e8f0;" valign="middle">
            <div style="display:inline-block;width:34px;height:34px;line-height:34px;background-color:#e0f2fe;border-radius:50%;color:#0284c7;font-size:14px;margin-bottom:6px;">🎫</div>
            <div style="font-size:11.5px;color:#64748b;margin-bottom:3px;">Ticket No.</div>
            <div style="font-size:15px;font-weight:800;color:#0f172a;">$ticketID$</div>
          </td>
          <td width="25%" style="padding:18px 10px;border-right:1px solid #e2e8f0;" valign="middle">
            <div style="display:inline-block;width:34px;height:34px;line-height:34px;background-color:#dcfce7;border-radius:50%;color:#16a34a;font-size:13px;margin-bottom:6px;">▶</div>
            <div style="font-size:11.5px;color:#64748b;margin-bottom:3px;">Current Action</div>
            <div style="font-size:15px;font-weight:800;color:#16a34a;">$currentaction$</div>
          </td>
          <td width="25%" style="padding:18px 10px;border-right:1px solid #e2e8f0;" valign="middle">
            <div style="display:inline-block;width:34px;height:34px;line-height:34px;background-color:#f3e8ff;border-radius:50%;color:#9333ea;font-size:14px;margin-bottom:6px;">⬆</div>
            <div style="font-size:11.5px;color:#64748b;margin-bottom:3px;">Next Action</div>
            <div style="font-size:15px;font-weight:800;color:#0f172a;">$nextaction$</div>
          </td>
          <td width="25%" style="padding:18px 10px;" valign="middle">
            <div style="display:inline-block;width:34px;height:34px;line-height:34px;background-color:#ffe4e6;border-radius:50%;color:#dc2626;font-size:14px;font-weight:bold;margin-bottom:6px;">!</div>
            <div style="font-size:11.5px;color:#64748b;margin-bottom:3px;">Priority</div>
            <div style="font-size:15px;font-weight:800;color:#dc2626;">$priority$</div>
          </td>
        </tr>
      </table>
    </div>`;
        }

        case 'message_body': {
          const bodyContent = (p.messageContent || template.innerMessageBody || '').replace(/\n/g, '<br/>');
          return `    <!-- LOWER MESSAGE NOTE -->
    <div style="padding:10px 28px;">
      <p style="font-size:14px;color:#475569;line-height:1.6;margin:0;">${bodyContent}</p>
    </div>`;
        }

        case 'signature_block': {
          return `    <!-- SIGNATURE BLOCK -->
    <div style="padding:20px 28px 24px 28px;font-size:14px;">
      <p style="margin:0 0 6px 0;color:#64748b;">${p.salutation || template.signoffSalutation || 'Kind regards,'}</p>
      <p style="margin:0;font-weight:800;color:#0f172a;font-size:16px;">${p.agentNameFormula || template.agentFormula || 'Team One'}</p>
    </div>`;
        }

        case 'footer_bar': {
          return `    <!-- BRANDED FOOTER -->
    <div style="background-color:#f8fafc;border-top:1px solid #e2e8f0;padding:16px 24px;border-bottom-left-radius:12px;border-bottom-right-radius:12px;position:relative;overflow:hidden;">
      <table width="100%" border="0" cellpadding="0" cellspacing="0">
        <tr>
          <td valign="middle">
            <table border="0" cellpadding="0" cellspacing="0">
              <tr>
                <td valign="middle" style="padding-right:12px;">
                  ${TRUSTYBYTES_LOGO_SVG_RAW('#0062D2', '#8CC63F')}
                </td>
                <td width="1" bgcolor="#cbd5e1" style="font-size:0;line-height:0;">&nbsp;</td>
                <td valign="middle" style="padding-left:12px;padding-right:12px;">
                  <span style="background-color:#0062D2;color:#ffffff;font-size:11px;font-weight:800;padding:4px 10px;border-radius:6px;letter-spacing:0.5px;display:inline-block;">TEAMONE</span>
                </td>
                <td width="1" bgcolor="#cbd5e1" style="font-size:0;line-height:0;">&nbsp;</td>
                <td valign="middle" style="padding-left:12px;">
                  <span style="font-size:13px;color:#64748b;">${p.footerSubtitle || 'One operational view for every team.'}</span>
                </td>
              </tr>
            </table>
          </td>
          <td align="right" valign="bottom" width="100" style="padding-right:0;">
            <svg width="80" height="35" viewBox="0 0 80 35" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M0 35C25 35 40 10 80 0V35H0Z" fill="url(#wave_grad)"/>
              <defs>
                <linearGradient id="wave_grad" x1="0" y1="0" x2="80" y2="35" gradientUnits="userSpaceOnUse">
                  <stop stop-color="#0284c7"/>
                  <stop offset="1" stop-color="#22c55e"/>
                </linearGradient>
              </defs>
            </svg>
          </td>
        </tr>
      </table>
    </div>`;
        }

        default:
          return '';
      }
    })
    .filter(Boolean)
    .join('\n\n');

  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8"/>
  <meta name="viewport" content="width=device-width, initial-scale=1.0"/>
  <title>${template.headerTopic || '$subject$'}</title>
</head>
<body style="margin:0;padding:24px 12px;background-color:#ECECEC;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif;">
  <div style="max-width:580px;margin:0 auto;background-color:#ffffff;border-radius:14px;box-shadow:0 4px 14px rgba(0,0,0,0.07);border:1px solid #E0E0E0;overflow:hidden;">
${renderedBlockHtmls}
  </div>
</body>
</html>`;
}

export function interpolateEmailTemplate(
  html: string,
  data: TemplateInterpolationData,
  template?: CardTemplateConfig
): string {
  if (!html) return '';

  const ticketId = data.ticketId || 'TB#01752';
  const cleanSubject = (data.subject || 'Support Ticket')
    .replace(/\s*\[(?:STD|TB|INC|REQ|CHG|[A-Z]+)#[a-z0-9_ -]+\]/gi, '')
    .replace(/^re:\s*/i, '')
    .trim() || 'Support Ticket';

  const clientName = data.clientName || 'Customer';
  const status = data.status || 'In Progress';
  const currentAction = data.currentAction || status;
  const nextAction = data.nextAction || 'Review and Resolve';
  const priority = data.priority || 'Medium';
  const agentName = data.agentName || template?.agentFormula || 'Team One';
  const relatedAsset = data.relatedAsset || 'None';
  const organization = data.organization || template?.organizationName || 'TrustyBytes Support';
  const brandName = data.brandName || template?.brandName || 'Team One';
  const teamName = data.teamName || template?.teamFormula || 'Team One';
  const queue = data.queue || data.teamName || 'Support Team';
  const date = data.date || new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  const ticketLink = data.ticketLink || `https://teamone.trustybytes.in/support/tickets/${encodeURIComponent(ticketId)}`;
  const supportEmail = data.fromEmail || data.supportEmail || template?.supportEmail || 'support@trustybytes.in';
  const fromFormatted = data.from || (data.senderName ? `${data.senderName} <${supportEmail}>` : supportEmail);
  const senderName = data.senderName || 'Support Team';

  const userMessage = (data.message || data.body || '').trim();
  const userMessageHtml = userMessage ? userMessage.replace(/\n/g, '<br/>') : '';

  let interpolated = html
    .replace(/\$ticketID\$/gi, ticketId)
    .replace(/\$ticket_id\$/gi, ticketId)
    .replace(/\$status\$/gi, status)
    .replace(/\$currentaction\$/gi, currentAction)
    .replace(/\$current_action\$/gi, currentAction)
    .replace(/\$nextaction\$/gi, nextAction)
    .replace(/\$next_action\$/gi, nextAction)
    .replace(/\$priority\$/gi, priority)
    .replace(/\$subject\$/gi, cleanSubject)
    .replace(/\$agent\$/gi, agentName)
    .replace(/\$agent_name\$/gi, agentName)
    .replace(/\$relatedasset\$/gi, relatedAsset)
    .replace(/\$related_asset\$/gi, relatedAsset)
    .replace(/\$asset\$/gi, relatedAsset)
    .replace(/\$client\$/gi, clientName)
    .replace(/\$client_name\$/gi, clientName)
    .replace(/\$organization\$/gi, organization)
    .replace(/\$org\$/gi, organization)
    .replace(/\$brand\$/gi, brandName)
    .replace(/\$team\$/gi, teamName)
    .replace(/\$team_name\$/gi, teamName)
    .replace(/\$queue\$/gi, queue)
    .replace(/\$date\$/gi, date)
    .replace(/\$ticketlink\$/gi, ticketLink)
    .replace(/\$ticket_link\$/gi, ticketLink)
    .replace(/\$support_email\$/gi, supportEmail)
    .replace(/\$supportemail\$/gi, supportEmail)
    .replace(/\$from_email\$/gi, supportEmail)
    .replace(/\$from\$/gi, fromFormatted)
    .replace(/\$sender_name\$/gi, senderName);

  // If user typed a message, handle token replacement
  if (/\$message\$|\$body\$|\$notes\$|\$content\$/i.test(interpolated)) {
    interpolated = interpolated
      .replace(/\$message\$/gi, userMessageHtml)
      .replace(/\$body\$/gi, userMessageHtml)
      .replace(/\$notes\$/gi, userMessageHtml)
      .replace(/\$content\$/gi, userMessageHtml);
  } else if (userMessageHtml) {
    // If the template didn't have a $message$ token, insert the composer note neatly above the assistance disclaimer or before signature
    const assistancePattern = /(<p[^>]*>If you require any further assistance[^<]*<\/p>)/i;
    const composerBlock = `<div style="margin:18px 0;font-size:14px;color:#334155;line-height:1.6;font-weight:400;">${userMessageHtml}</div>`;

    if (assistancePattern.test(interpolated)) {
      interpolated = interpolated.replace(assistancePattern, `${composerBlock}\n$1`);
    } else {
      const signaturePattern = /(<div[^>]*border-top:[^>]*>[\s\S]*?Kind regards[\s\S]*?<\/div>)/i;
      if (signaturePattern.test(interpolated)) {
        interpolated = interpolated.replace(signaturePattern, `${composerBlock}\n$1`);
      }
    }
  }

  return interpolated;
}

export function renderEmailCard(
  template: CardTemplateConfig | null | undefined,
  data: TemplateInterpolationData
): string {
  const activeTemplate = template || resolveActiveEmailTemplate(data.currentAction || data.status);
  const baseHtml = activeTemplate.customHtml !== undefined && activeTemplate.customHtml !== ''
    ? activeTemplate.customHtml
    : generateBaseTemplateHtml(activeTemplate);

  return interpolateEmailTemplate(baseHtml, data, activeTemplate);
}
